// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CostBreakdownFields } from '@/accounting/components/cost-breakdown-fields';
import {
  AccountingCostFormValues,
  costBreakdownParameters,
  toAccountingCostFormValues,
} from '@/accounting/utilities/cost-breakdown-form';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { FormCurrencySelect } from '@/components/form-currency-select';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormNumberTextField } from '@/components/form-number-text-field';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  MAX_USEFUL_LIFE_MONTHS,
  MIN_USEFUL_LIFE_MONTHS,
} from '@/constants/accounting-form-fields';
import {
  OrgDefaultCurrencyCode,
  orgDefaultCurrencyCodeSchema,
} from '@/constants/currency';
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import { clearableCurrencyInputSchema } from '@/currency/api/types';
import { padAmountToMinorUnit } from '@/currency/utilities/serialize';
import { toCurrencyInputValueFromString } from '@/currency/utilities/to-form-value';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { ItemDetail } from '@/items/api/types';
import {
  UpdateItemAccountingParameters,
  updateItemAccounting,
} from '@/items/api/update-item-accounting';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, BoxProps, Button, Collapse, Stack, Tooltip } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Decimal } from 'decimal.js';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  usefulLife: z
    .string()
    .nullable()
    .refine(
      (value) => {
        if (value === null || value === '') return true;
        const num = Number(value);
        return (
          !isNaN(num) &&
          num >= MIN_USEFUL_LIFE_MONTHS &&
          num <= MAX_USEFUL_LIFE_MONTHS
        );
      },
      {
        message: `Useful life must be between ${MIN_USEFUL_LIFE_MONTHS} and ${MAX_USEFUL_LIFE_MONTHS} months`,
      }
    ),
  salvageValue: clearableCurrencyInputSchema,
  simpleCostBasis: clearableCurrencyInputSchema,
  billOfMaterials: clearableCurrencyInputSchema,
  directLabor: clearableCurrencyInputSchema,
  allocatedIndirect: clearableCurrencyInputSchema,
  freightInbound: clearableCurrencyInputSchema,
  freightOutbound: clearableCurrencyInputSchema,
  installation: clearableCurrencyInputSchema,
  tax: clearableCurrencyInputSchema,
  tariffs: clearableCurrencyInputSchema,
  interest: clearableCurrencyInputSchema,
  // The selector only offers the app-allowed currencies, and the backend only
  // accepts those, so the form validates against the same four rather than the
  // 26-code recognized registry.
  costCurrencyCode: orgDefaultCurrencyCodeSchema,
});

// The form holds money fields as their input shape ({ amount, isoCode });
// FormOutput is the post-resolver shape where each amount is a Currency or null.
interface FormData extends AccountingCostFormValues {
  costCurrencyCode: OrgDefaultCurrencyCode;
}

/**
 * Resolves the accounting currency code to seed the form with. A stored code outside the
 * app-allowed set has no option to select and no symbol the amount fields can
 * render, so the organization default stands in for it.
 */
function toCurrencyCode(
  stored: string | null,
  orgDefault: OrgDefaultCurrencyCode
): OrgDefaultCurrencyCode {
  const parsed = orgDefaultCurrencyCodeSchema.safeParse(stored);
  return parsed.success ? parsed.data : orgDefault;
}

type FormOutput = z.output<typeof formSchema>;

interface Props extends Pick<BoxProps, 'sx'> {
  item: ItemDetail;
  /** Route-specific banner rendered above the form, inside the card body. */
  infoBanner?: ReactNode;
}

/**
 * Form component for editing item accounting defaults.
 * Supports both simple cost basis and advanced cost breakdown modes.
 */
export function ItemAccountingForm(props: Props) {
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const orgDefaultCurrencyCode = user.organization.defaultCurrency;

  const [userToggledToAdvanced, setUserToggledToAdvanced] = useState(false);
  const [toggleCount, setToggleCount] = useState(0);

  const mutation = useMutation({
    mutationFn: (parameters: UpdateItemAccountingParameters) => {
      return updateItemAccounting(props.item.key, parameters);
    },
    onSuccess: (data) => {
      // Update item query cache
      queryClient.setQueryData(['item', props.item.key], {
        item: data.item,
        metadata: data.metadata,
      });

      const updatedCurrencyCode = toCurrencyCode(
        data.item.accounting.costCurrencyCode,
        orgDefaultCurrencyCode
      );

      const resetData: FormData = {
        ...toAccountingCostFormValues(
          data.item.accounting,
          updatedCurrencyCode
        ),
        costCurrencyCode: updatedCurrencyCode,
      };

      reset(resetData);
    },
    onError: (error) => {
      const apiError = handleAPIError(error);

      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.accounting_invalid_useful_life
      ) {
        setError('usefulLife', {
          type: 'custom',
          message: `Useful life must be between ${MIN_USEFUL_LIFE_MONTHS} and ${MAX_USEFUL_LIFE_MONTHS} months`,
        });
      }
    },
  });

  const defaultValues = useMemo<FormData>(() => {
    const currencyCode = toCurrencyCode(
      props.item.accounting.costCurrencyCode,
      orgDefaultCurrencyCode
    );

    const breakdownValues = [
      props.item.accounting.billOfMaterials,
      props.item.accounting.directLabor,
      props.item.accounting.allocatedIndirect,
      props.item.accounting.freightInbound,
      props.item.accounting.freightOutbound,
      props.item.accounting.installation,
      props.item.accounting.tax,
      props.item.accounting.tariffs,
      props.item.accounting.interest,
    ];

    const hasBreakdownData = breakdownValues.some((field) => field !== null);

    const costFormValues = toAccountingCostFormValues(
      props.item.accounting,
      currencyCode
    );

    if (hasBreakdownData) {
      const sum = breakdownValues.reduce((total, value) => {
        if (value === null) return total;
        return total.plus(value.amount);
      }, new Decimal(0));

      costFormValues.simpleCostBasis = toCurrencyInputValueFromString(
        sum.gt(0) ? sum.toFixed(2) : null,
        currencyCode
      );
    }

    return {
      ...costFormValues,
      costCurrencyCode: currencyCode,
    };
  }, [props.item.accounting, orgDefaultCurrencyCode]);

  const methods = useForm<FormData, unknown, FormOutput>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const { control, formState, reset, setError, handleSubmit, watch, setValue } =
    methods;

  const { dirtyFields, isDirty } = formState;

  const breakdownAmounts = watch([
    'billOfMaterials.amount',
    'directLabor.amount',
    'allocatedIndirect.amount',
    'freightInbound.amount',
    'freightOutbound.amount',
    'installation.amount',
    'tax.amount',
    'tariffs.amount',
    'interest.amount',
  ]);

  const costCurrencyCode = watch('costCurrencyCode');

  // The accounting currency is a persisted field, so changing it is meaningful on
  // its own — even with every cost blank the new currency is stored. Surface the
  // save action for a currency change or any amount/useful-life change.
  const hasDirtyCostAmount = [
    dirtyFields.salvageValue,
    dirtyFields.simpleCostBasis,
    dirtyFields.billOfMaterials,
    dirtyFields.directLabor,
    dirtyFields.allocatedIndirect,
    dirtyFields.freightInbound,
    dirtyFields.freightOutbound,
    dirtyFields.installation,
    dirtyFields.tax,
    dirtyFields.tariffs,
    dirtyFields.interest,
  ].some((field) => Boolean(field?.amount));
  const hasMeaningfulChange =
    isDirty &&
    (hasDirtyCostAmount ||
      Boolean(dirtyFields.usefulLife) ||
      Boolean(dirtyFields.costCurrencyCode));

  const showAdvancedBreakdown =
    userToggledToAdvanced ||
    breakdownAmounts.some((field) => field !== null && field !== '');

  const calculatedSum = useMemo(() => {
    const allFieldsNull = breakdownAmounts.every(
      (field) => field === null || field === ''
    );

    if (allFieldsNull) return null;

    const sum = breakdownAmounts.reduce((total, value) => {
      const amount = value ? parseCurrencyAmount(value) : null;
      return amount === null ? total : total.plus(amount);
    }, new Decimal(0));

    return padAmountToMinorUnit(sum, costCurrencyCode);
  }, [breakdownAmounts, costCurrencyCode]);

  useEffect(() => {
    if (showAdvancedBreakdown) {
      setValue('simpleCostBasis.amount', calculatedSum ?? '', {
        shouldDirty: false,
        shouldValidate: false,
        shouldTouch: false,
      });
    }
  }, [calculatedSum, showAdvancedBreakdown, setValue]);

  useEffect(() => {
    if (!isDirty) {
      reset(defaultValues);
    }
  }, [defaultValues, isDirty, reset]);

  const handleValidSubmission: SubmitHandler<FormOutput> = (data) => {
    const parameters: UpdateItemAccountingParameters = costBreakdownParameters(
      data,
      dirtyFields,
      showAdvancedBreakdown
    );

    if (dirtyFields.costCurrencyCode) {
      parameters.costCurrencyCode = data.costCurrencyCode;
    }

    if (dirtyFields.usefulLife) {
      parameters.usefulLife =
        data.usefulLife === null ? null : Number(data.usefulLife);
    }

    if (hasMeaningfulChange) {
      mutation.mutate(parameters);
    }
  };

  const handleClickReset = () => {
    reset(defaultValues);

    const originalBreakdownValues = [
      props.item.accounting.billOfMaterials,
      props.item.accounting.directLabor,
      props.item.accounting.allocatedIndirect,
      props.item.accounting.freightInbound,
      props.item.accounting.freightOutbound,
      props.item.accounting.installation,
      props.item.accounting.tax,
      props.item.accounting.tariffs,
      props.item.accounting.interest,
    ];

    const originalHasBreakdownData = originalBreakdownValues.some(
      (field) => field !== null
    );

    setUserToggledToAdvanced(originalHasBreakdownData);
    setToggleCount((prev) => prev + 1);
  };

  const handleToggleAdvanced = () => {
    setToggleCount((prev) => prev + 1);

    if (!showAdvancedBreakdown) {
      setUserToggledToAdvanced(true);

      const currentCostBasis = watch('simpleCostBasis.amount');
      setValue('billOfMaterials.amount', currentCostBasis, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
      setValue('directLabor.amount', '');
      setValue('allocatedIndirect.amount', '');
      setValue('freightInbound.amount', '');
      setValue('freightOutbound.amount', '');
      setValue('installation.amount', '');
      setValue('tax.amount', '');
      setValue('tariffs.amount', '');
      setValue('interest.amount', '');
    } else {
      setUserToggledToAdvanced(false);

      setValue('simpleCostBasis.amount', calculatedSum ?? '', {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });

      setValue('billOfMaterials.amount', '', { shouldDirty: true });
      setValue('directLabor.amount', '', { shouldDirty: true });
      setValue('allocatedIndirect.amount', '', { shouldDirty: true });
      setValue('freightInbound.amount', '', { shouldDirty: true });
      setValue('freightOutbound.amount', '', { shouldDirty: true });
      setValue('installation.amount', '', { shouldDirty: true });
      setValue('tax.amount', '', { shouldDirty: true });
      setValue('tariffs.amount', '', { shouldDirty: true });
      setValue('interest.amount', '', { shouldDirty: true });
    }
  };

  return (
    <FormProvider {...methods}>
      <HardfinTabCardWrapper
        title="Accounting"
        actions={
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 2 }}>
            {/* Wrapped in a span so the header matches the asset form, whose locked
              currency select renders inside a span for its disabled tooltip. */}
            <span>
              <FormCurrencySelect
                name="costCurrencyCode"
                control={control}
                label="Default accounting currency"
                sx={{ minWidth: 220, my: 1 }}
              />
            </span>
            <Tooltip
              arrow
              placement="top"
              title="Default accounting currency determines the default currency for how costs are captured for an asset of this item. (This controls the default currency used to log costs for the asset, it does not convert foreign currencies.)"
            >
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Stack>
        }
      >
        {props.infoBanner}
        <Box
          component="form"
          onSubmit={handleSubmit(handleValidSubmission)}
          sx={props.sx}
        >
          <SupportSnackbar
            isOpen={
              mutation.isError &&
              handleAPIError(mutation.error).apiStatusCode !==
                ERROR_CODE_MAPPING.accounting_invalid_useful_life
            }
            onClose={mutation.reset}
            message="An error occurred while updating accounting details."
          />
          <Box
            display="grid"
            gridTemplateColumns="1fr 1fr"
            columnGap={6}
            rowGap={3}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormCurrencyTextField
                key={`simpleCostBasis-${toggleCount}`}
                fullWidth
                label="Cost basis"
                name="simpleCostBasis"
                control={control}
                lockTo={costCurrencyCode}
                compact
                disabled={showAdvancedBreakdown}
              />
              <Tooltip
                arrow
                placement="top"
                title="Default cost basis for assets of this item type"
              >
                <InfoOutlined fontSize="small" color="action" />
              </Tooltip>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormNumberTextField
                fullWidth
                label="Useful life (months)"
                name="usefulLife"
                control={control}
                allowNegative={false}
                maximumFractionDigits={0}
              />
              <Tooltip
                arrow
                placement="top"
                title={`Default useful life in months for assets of this item type (must be between ${MIN_USEFUL_LIFE_MONTHS} and ${MAX_USEFUL_LIFE_MONTHS} months)`}
              >
                <InfoOutlined fontSize="small" color="action" />
              </Tooltip>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormCurrencyTextField
                fullWidth
                label="Salvage value"
                name="salvageValue"
                control={control}
                lockTo={costCurrencyCode}
                compact
              />
              <Tooltip
                arrow
                placement="top"
                title="Default salvage value for assets of this item type"
              >
                <InfoOutlined fontSize="small" color="action" />
              </Tooltip>
            </Stack>
          </Box>

          <Box paddingTop={3}>
            <Button
              variant={showAdvancedBreakdown ? 'text' : 'outlined'}
              onClick={handleToggleAdvanced}
              size="small"
            >
              {showAdvancedBreakdown ? 'Hide' : 'Show'} advanced cost breakdown
            </Button>
          </Box>

          <Collapse in={showAdvancedBreakdown}>
            <CostBreakdownFields
              costCurrencyCode={costCurrencyCode}
              toggleCount={toggleCount}
            />
          </Collapse>
          <Collapse in={hasMeaningfulChange} unmountOnExit>
            <Stack
              direction="row"
              spacing={1}
              paddingTop={3}
              justifyContent="flex-end"
            >
              <Button onClick={handleClickReset}>Reset</Button>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={mutation.isPending}
              >
                Save
              </Button>
            </Stack>
          </Collapse>
        </Box>
      </HardfinTabCardWrapper>
    </FormProvider>
  );
}
